import { SCHEDULE_MAX_DAYS, SCHEDULE_MIN_LEAD_MINUTES, SCHEDULE_STEP_MINUTES } from '../constants';

const SERVER_DATETIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/;
const DUE_POLL_INTERVAL_MS = 10_000;
const MAX_POLL_INTERVAL_MS = 60_000;
const DUE_BUFFER_MS = 2_000;
const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

// API は Time.zone (Asia/Tokyo) の 'YYYY-MM-DD HH:mm' で返す
export function parseServerDateTime(value: string | null | undefined): Date | null {
  const match = value ? SERVER_DATETIME_PATTERN.exec(value) : null;
  if (!match) return null;
  const [, year, month, day, hours, minutes] = match;
  return new Date(`${year}-${month}-${day}T${hours}:${minutes}:00+09:00`);
}

export function formatScheduledAt(value: string | null | undefined): string {
  const match = value ? SERVER_DATETIME_PATTERN.exec(value) : null;
  if (!match) return value ?? '';
  const [, year, month, day, hours, minutes] = match;
  return `${year}年${Number(month)}月${Number(day)}日 ${hours}:${minutes}`;
}

export function scheduledRefetchInterval(scheduledAts: Array<string | null>): number | false {
  const times = scheduledAts
    .map(parseServerDateTime)
    .filter((date): date is Date => date !== null)
    .map((date) => date.getTime());
  if (times.length === 0) return false;

  const untilDue = Math.min(...times) - Date.now();
  if (untilDue <= 0) return DUE_POLL_INTERVAL_MS;
  return Math.min(untilDue + DUE_BUFFER_MS, MAX_POLL_INTERVAL_MS);
}

function padTime(value: number) {
  return String(value).padStart(2, '0');
}

export function formatMailListDate(value: string | null | undefined): string {
  return formatListDate(parseServerDateTime(value));
}

export function formatListDate(date: Date | null, now = new Date()): string {
  if (!date || Number.isNaN(date.getTime())) return '-';
  if (date.toDateString() === now.toDateString()) {
    return `${padTime(date.getHours())}:${padTime(date.getMinutes())}`;
  }
  if (date.getFullYear() === now.getFullYear()) {
    return `${date.getMonth() + 1}月${date.getDate()}日`;
  }
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
}

export function formatMailScheduleListDate(value: string | null | undefined): string {
  const date = parseServerDateTime(value);
  if (!date) return '-';
  return `${date.getMonth() + 1}月${date.getDate()}日${padTime(date.getHours())}:${padTime(date.getMinutes())}`;
}

export function formatMailHeadingDate(value: string | null | undefined): string {
  const date = parseServerDateTime(value);
  if (!date) return '';
  return `${date.getMonth() + 1}月${date.getDate()}日(${WEEKDAYS[date.getDay()]}) ${date.getHours()}:${padTime(date.getMinutes())}`;
}

export function formatDateTimeWithWeekday(date: Date): string {
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日(${WEEKDAYS[date.getDay()]}) ${padTime(date.getHours())}:${padTime(date.getMinutes())}`;
}

export function toMinuteIsoString(date: Date): string {
  const truncated = new Date(date);
  truncated.setSeconds(0, 0);
  return truncated.toISOString();
}

export function getScheduleBounds(now = new Date()) {
  return {
    min: new Date(now.getTime() + SCHEDULE_MIN_LEAD_MINUTES * 60 * 1000),
    max: new Date(now.getTime() + SCHEDULE_MAX_DAYS * 24 * 60 * 60 * 1000),
  };
}

export function validateScheduledAt(date: Date | null): string | null {
  if (!date) return '送信日時を選択してください。';
  const { min, max } = getScheduleBounds();
  if (date.getTime() < min.getTime() || date.getTime() > max.getTime()) {
    return `送信日時は現在から${SCHEDULE_MIN_LEAD_MINUTES}分以上先、${SCHEDULE_MAX_DAYS}日以内で指定してください。`;
  }
  if (date.getMinutes() % SCHEDULE_STEP_MINUTES !== 0) {
    return `送信日時は${SCHEDULE_STEP_MINUTES}分刻みで指定してください。`;
  }
  return null;
}
